import { CLUSTER_LEVEL_COPY } from '../../data/resultCopy';
import type { DataModel } from '../../data/loadDataModel';
import { generateRandomSeed } from '../shuffle/seededRandom';
import { CLUSTER_IDS, type ClusterId } from '../../types/ids';
import type { ClusterResult, EvaluationContext, TestResult } from '../../types/result';
import type { TestSession } from '../../types/session';
import {
  calculateOverallPercentage,
  calculateOverallScore,
} from '../scoring/calculateOverallScore';
import { calculateClusterScores } from '../scoring/calculateClusterScores';
import { determineClusterBand, determineOverallBand } from '../scoring/determineBands';
import { detectTensions } from '../tensions/detectTensions';
import { detectStrengths } from '../insights/detectStrengths';
import { detectWeakPoints } from '../insights/detectWeakPoints';
import { determineChannelRole } from '../recommendations/determineChannelRole';
import { determineVisibilityModel } from '../recommendations/determineVisibilityModel';
import { collectRecommendations } from '../recommendations/collectRecommendations';
import { prioritizeRecommendations } from '../recommendations/prioritizeRecommendations';
import { composeResultSummary } from './composeResultSummary';

/**
 * Baut aus einer vollständigen Session das gesamte Ergebnisobjekt (Abschnitt 37, Kernfunktion
 * `buildTestResult`). Reine Funktion: Session und Datenmodell werden nicht verändert (DAT-003).
 */
export function buildTestResult(session: TestSession, dataModel: DataModel): TestResult {
  const { clusters, clusterMatrix, resultBands, tensionRules, recommendationRules, appConfig } =
    dataModel;

  // 1+2: Gesamt-Score
  const score = calculateOverallScore(session.answers);
  const maximum = appConfig.test.maximumOverallScore;
  const percentage = calculateOverallPercentage(score, maximum);
  const overallBandDefinition = determineOverallBand(score, resultBands.overall);

  // 3+4: Cluster berechnen und Ergebnisstufen bestimmen
  const rawClusterResults = calculateClusterScores(session.answers, clusterMatrix);
  const clusterResults = {} as Record<ClusterId, ClusterResult>;
  for (const clusterId of CLUSTER_IDS) {
    const raw = rawClusterResults[clusterId];
    const clusterDefinition = clusters.find((cluster) => cluster.id === clusterId);
    if (!clusterDefinition) throw new Error(`Unbekannter Cluster: ${clusterId}`);
    const bandDefinition = determineClusterBand(raw.percentage, resultBands.cluster);
    const copy = CLUSTER_LEVEL_COPY[clusterId][bandDefinition.id];
    clusterResults[clusterId] = {
      clusterId,
      title: clusterDefinition.title,
      rawScore: raw.rawScore,
      maximum: raw.maximum,
      percentage: raw.percentage,
      level: bandDefinition.id,
      label: bandDefinition.label,
      summary: copy.summary,
      detail: copy.detail,
      opportunities: copy.opportunities,
      risks: copy.risks,
      contributingQuestions: raw.contributingQuestions,
    };
  }

  const evaluationContext: EvaluationContext = {
    answers: session.answers,
    context: session.context,
    overall: { score, maximum, percentage },
    clusters: clusterResults,
  };

  // 5: Spannungsfelder
  const tensions = detectTensions(evaluationContext, tensionRules);

  // 6+7: Stärken und Engpässe
  const strengths = detectStrengths(
    evaluationContext,
    clusterMatrix,
    recommendationRules.rankingRules,
  );
  const weakPoints = detectWeakPoints(
    evaluationContext,
    clusterMatrix,
    recommendationRules.rankingRules,
  );

  // 8: Kanalrolle
  const channelRole = determineChannelRole(evaluationContext, recommendationRules.channelRoleRules);

  // 9: Sichtbarkeitsmodell
  const visibilityModel = determineVisibilityModel(
    evaluationContext,
    recommendationRules.visibilityModelRules,
    dataModel.contextQuestions,
  );

  // 10+11: Empfehlungen sammeln, deduplizieren und priorisieren
  const ranked = collectRecommendations(
    tensions,
    weakPoints,
    recommendationRules.rankingRules.weakPoints.businessCriticalQuestionIds,
  );
  const actionPlan = prioritizeRecommendations(
    ranked,
    recommendationRules.recommendations,
    appConfig.recommendations.maximumPerGroup,
  );

  // 12: Ergebniszusammenfassung
  const summary = composeResultSummary(
    overallBandDefinition,
    CLUSTER_IDS.map((id) => clusterResults[id]),
    tensions,
    weakPoints,
    channelRole,
  );

  // 13: vollständiges Ergebnisobjekt
  return {
    resultId: generateRandomSeed(),
    sessionId: session.sessionId,
    schemaVersion: session.schemaVersion,
    scoringVersion: session.scoringVersion,
    createdAt: new Date().toISOString(),
    overall: {
      score,
      maximum,
      percentage,
      band: overallBandDefinition.id,
      label: overallBandDefinition.label,
    },
    clusters: CLUSTER_IDS.map((id) => clusterResults[id]),
    tensions,
    strengths,
    weakPoints,
    recommendedChannelRole: channelRole,
    recommendedVisibilityModel: visibilityModel,
    summary,
    actionPlan,
    sourceAnswers: session.answers,
    context: session.context,
  };
}
